import * as moment from 'moment';
import 'moment-timezone';
import { isArray, isNumber, isObject, isString } from 'util';
import { has, isEqual } from 'lodash';

import { DropdownItem } from '@gosuite/selects';

import { KeyValueObject } from '../../../../../shared/models/key-value-object';
import { SearchformPaModel } from '../searchform/searchform-pa.model';
import { SearchformNhModel } from '../searchform/searchform-nh.model';
import { SearchformNfModel } from '../searchform/searchform-nf.model';
import { GT_BOOKING_CODE_PREFIX, GT_CATEGORY_PREFIX, GT_INBOUND_FLIGHT_TIMES_PREFIX, GT_OUTBOUND_FLIGHT_TIMES_PREFIX, GT_RATING_PREFIX } from '../../data/global-type-prefixes';
import { GT_RAIL_AND_FLY } from '../../types/rail-and-fly-type';
import { GT_SEA_VIEW } from '../../types/sea-view-type';
import { GT_HYGIENE } from '../../types/hygiene-type';
import { GT_NON_STOP_FLIGHT } from '../../types/non-stop-flight-type';
import { AdvancedSearchformDistance, DEFAULT_ADVANCED_SEARCHFORM_DISTANCE } from '../../../searchform/advanced-search-dialog/shared/advanced-searchform.model';
import { BEACH_ATTRIBUTES_MAPPING, DISTANCE_ATTRIBUTES_MAPPING, HOTEL_FACILITY_ATTRIBUTES_MAPPING, HOTEL_TYPES_MAPPING, POOL_ATTRIBUTES_MAPPING, SPORT_ATTRIBUTES_MAPPING, TRANSFER_ATTRIBUTES_MAPPING } from '../../../searchform/advanced-search-dialog/shared/mapping';
import { DISTANCE_TYPES, getDistanceIndexToValue } from '../../../searchform/advanced-search-dialog/shared/distance';
import { MultiRoom, Occupancy } from '../../../searchform/multi-room-dialog/multi-room-utils';
import { MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS } from '../../../../../shared/help/multi-room';



export interface QueryModel {
	adultsNumber: number;
	startDate: string;
	endDate: string;
	type: string;
	limit: number;
	offset: number;
	orderBy: string;
	orderDirection: string;
	tags?: string;
	childrenAges?: string;
	id?: string;
	geoJson?: string;
	hotelName?: string;
	bookingCode?: string;
	maxPrice?: number;
	topsFlops?: Array<string>;
	status?: string;
	multiRoomOccupancy?: string;
	occupancy?: Occupancy;
}

export const InitialQueryState: QueryModel = {
	adultsNumber: 2,
	startDate: moment().tz('Europe/Berlin').add(14, 'days').format('YYYY-MM-DD'),
	endDate: moment().tz('Europe/Berlin').add(28, 'days').format('YYYY-MM-DD'),
	type: 'PA',
	tags: '',
	limit: 500,
	offset: 0,
	orderBy: 'price',
	orderDirection: 'asc'
};

export class Query implements QueryModel {
	public adultsNumber: number;
	public startDate: string;
	public endDate: string;
	public type: string;
	public limit: number;
	public offset: number;
	public orderBy: string;
	public orderDirection: string;
	public tags?: string;
	public childrenAges?: string;
	public id?: string;
	public geoJson?: string;
	public hotelName?: string;
	public bookingCode?: string;
	public maxPrice?: number;
	public topsFlops?: Array<string>;
	public status?: string;
	public multiRoomOccupancy?: string;
	public occupancy?: Occupancy;

	public constructor(query: QueryModel = null) {
		this.adultsNumber = isObject(query) && isNumber(query.adultsNumber) && !isNaN(query.adultsNumber) && query.adultsNumber > 0
			? query.adultsNumber
			: InitialQueryState.adultsNumber;

		this.startDate = isObject(query) && query.startDate && moment(query.startDate).isValid()
			? moment(query.startDate).format('YYYY-MM-DD')
			: InitialQueryState.startDate;

		this.endDate = isObject(query) && query.endDate && moment(query.endDate).isValid()
			? moment(query.endDate).format('YYYY-MM-DD')
			: InitialQueryState.endDate;

		this.type = isObject(query) && isString(query.type) && ['PA', 'NH', 'NF'].indexOf(query.type.toUpperCase()) !== -1
			? query.type.toUpperCase()
			: InitialQueryState.type;

		this.limit = isObject(query) && isNumber(query.limit) && !isNaN(query.limit) && query.limit > 0
			? query.limit
			: InitialQueryState.limit;

		this.offset = isObject(query) && isNumber(query.offset) && !isNaN(query.offset) && query.offset >= 0
			? query.offset
			: InitialQueryState.offset;

		this.orderBy = isObject(query) && isString(query.orderBy) && ['price'].indexOf(query.orderBy) !== -1
			? query.orderBy
			: InitialQueryState.orderBy;

		this.orderDirection = isObject(query) && isString(query.orderDirection) && ['asc', 'desc'].indexOf(query.orderDirection.toLowerCase())
			? query.orderDirection.toLowerCase()
			: InitialQueryState.orderDirection;

		if (isObject(query) && isString(query.tags) && query.tags.length) {
			this.tags = query.tags;
		} else {
			this.tags = '';
		}

		if (isObject(query) && isString(query.multiRoomOccupancy) && query.multiRoomOccupancy.length > 0) {
			this.multiRoomOccupancy = query.multiRoomOccupancy;
		} else {
			this.multiRoomOccupancy = '';
		}

		if (isObject(query) && isObject(query.occupancy)) {
			this.occupancy = query.occupancy;
		} else {
			this.occupancy = null;
		}

		if (isObject(query) && isString(query.childrenAges) && query.childrenAges.length) {
			this.childrenAges = query.childrenAges;
		}

		if (isObject(query) && isString(query.geoJson) && query.geoJson.length) {
			this.geoJson = query.geoJson;
		}

		if (isObject(query) && isString(query.hotelName) && query.hotelName.length) {
			this.hotelName = query.hotelName;
		}

		if (isObject(query) && isString(query.bookingCode) && query.bookingCode.length) {
			this.bookingCode = query.bookingCode;
		}

		if (isObject(query) && isString(query.id) && query.id.length) {
			this.id = query.id;
		}

		if (isObject(query) && isNumber(query.maxPrice) && !isNaN(query.maxPrice) && query.maxPrice > 0) {
			this.maxPrice = query.maxPrice;
		}

		if (isObject(query) && isArray(query.topsFlops) && query.topsFlops.length) {
			this.topsFlops = query.topsFlops;
		}

		if (isObject(query) && isString(query.status)) {
			this.status = query.status;
		}
	}

	public toPostParams(): FormData {
		const query: KeyValueObject<string> = {
			'adult_count': this.adultsNumber.toString(),
			'start_date': this.startDate,
			'end_date': this.endDate,
			'type': this.type,
			'limit': this.limit.toString(),
			'offset': this.offset.toString(),
			'orderBy': this.orderBy,
			'orderDirection': this.orderDirection,
			'currency': 'EUR',
		};

		if (isString(this.tags) && this.tags.length > 0) {
			query['tags'] = this.tags;
		}

		if (isString(this.multiRoomOccupancy) && this.multiRoomOccupancy.length > 0) {
			query['multiRoomOccupancy'] = this.multiRoomOccupancy;
		}

		if (isString(this.childrenAges) && this.childrenAges.length) {
			query['children_ages'] = this.childrenAges;
		}

		if (isString(this.id) && this.id.length) {
			query['id'] = this.id;
		}

		if (isString(this.geoJson) && this.geoJson.length) {
			query['geojson'] = this.geoJson;
		}

		if (isString(this.hotelName) && this.hotelName.length) {
			query['hotel_name'] = this.hotelName;
		}

		if (isString(this.bookingCode) && this.bookingCode.length) {
			query['hotel_code'] = this.bookingCode;
		}

		if (isNumber(this.maxPrice) && !isNaN(this.maxPrice) && this.maxPrice > 0) {
			query['maxPrice'] = this.maxPrice.toString();
		}

		if (this.status === 'XX' || this.status === 'OK') {
			query.status = this.status;
		}

		const formData: FormData = new FormData();

		Object.keys(query).forEach(key => formData.set(key, query[key]));

		return formData;
	}
}

export const createQueryFromSearchformPaModel: (
	form: SearchformPaModel,
	availableTourOperators?: Array<DropdownItem>
) => Query = function (
	form: SearchformPaModel,
	availableTourOperators: Array<DropdownItem> = []
) {
	availableTourOperators = isArray(availableTourOperators) && !!(availableTourOperators.length) ? availableTourOperators : [];

	if (isObject(form)) {
		let tags: Array<string> = [];
		const customTags: Array<string> = [];
		let hotelName: string = null;
		let bookingCode: string = null;

		if (isArray(form.hotels) && form.hotels.length) {
			const custom: string = form.hotels.filter(item => {
				return isString(item.rawValue) && item.rawValue.startsWith('T:');
			}).map(item => item.rawValue.replace('T:', '')).join('|');

			if (custom.length) {
				custom.split('|').forEach(tagString => {
					customTags.push(tagString);
				});
			}

			const bookingCodes: Array<string> = form.hotels.filter(item => {
				return (isString(item.value) && item.value.startsWith(GT_BOOKING_CODE_PREFIX)) || (isString(item.rawValue) && item.rawValue.startsWith('#')) || (isString(item.rawValue) && item.rawValue.startsWith(':'));
			}).map(item => item.rawValue.replace('#', GT_BOOKING_CODE_PREFIX).replace(':', GT_BOOKING_CODE_PREFIX));

			if (bookingCodes.length === 1) {
				bookingCode = bookingCodes[0].replace('CODE-', '');
			} else if (bookingCodes.length > 1) {
				tags.push(bookingCodes.join(':'));
			}

			let bombyxHotelIds: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && (item.value.startsWith('GT01-') || item.value.startsWith('BT01-'));
			}).map(item => item.value);

			const manualSelectedBombyxHotelIds: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && ((item.value.startsWith('HOTELNAME-') && Number(item.rawValue)) || Number(item.value));
			}).map(item => 'GT01-' + item.rawValue);

			bombyxHotelIds = bombyxHotelIds.concat(manualSelectedBombyxHotelIds);

			if (bombyxHotelIds.length) {
				const hotelIds: Array<string> = [];

				bombyxHotelIds.forEach(hotelId => {
					hotelIds.push(hotelId);

					if (hotelId.startsWith('GT01-')) {
						hotelIds.push(hotelId.replace('GT01-', 'BT01-'));
					} else if (hotelId.startsWith('BT01-')) {
						hotelIds.push(hotelId.replace('BT01-', 'GT01-'));
					}
				});

				tags.push(hotelIds.join(':'));
			}

			const hotelNameSearchStrings: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && item.value.startsWith('HOTELNAME-') && !Number(item.rawValue) && !item.rawValue.startsWith('#') && !item.rawValue.startsWith(':') && !item.rawValue.startsWith('T:');
			}).map(item => item.rawValue);

			if (hotelNameSearchStrings.length) {
				// TODO: check if we could search all search-strings
				hotelName = hotelNameSearchStrings[0];
			}
		}

		if (isArray(form.departureAirports) && form.departureAirports.length) {
			tags.push(form.departureAirports.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationAirports) && form.destinationAirports.length) {
			tags.push(form.destinationAirports.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationRegions) && form.destinationRegions.length) {
			tags.push(form.destinationRegions.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationCities) && form.destinationCities.length) {
			tags.push(form.destinationCities.map(item => item.value).join(':'));
		}

		if (isNumber(form.minDuration) && !isNaN(form.minDuration)) {
			const duration: Array<string> = [];
			const maxDuration: number = isNumber(form.maxDuration) && !isNaN(form.maxDuration) && form.maxDuration >= form.minDuration
				? form.maxDuration
				: form.minDuration + 2;

			for (let i = form.minDuration; i <= maxDuration; i++) {
				duration.push('DUR-' + i.toString());
			}

			tags.push(duration.join(':'));
		}

		if (isNumber(form.category) && !isNaN(form.category)) {
			const minCategory: number = form.category * 10;
			const category: Array<string> = [];

			for (let i = minCategory; i <= 70; i += 5) {
				if (i % 10 === 0) {
					category.push(GT_CATEGORY_PREFIX + (i / 10));
				} else {
					category.push(GT_CATEGORY_PREFIX + i);
				}
			}

			tags.push(category.join(':'));
		}

		if (isObject(form.occupancy)) {
			if (isObject(form.occupancy.occupancy1.roomType)) {
				tags.push(form.occupancy.occupancy1.roomType.id);
			}

			if (isObject(form.occupancy.occupancy1.boardType)) {
				tags.push(form.occupancy.occupancy1.boardType.id);
			}
		} else {
			if (isArray(form.boardTypes) && form.boardTypes.length) {
				tags.push(form.boardTypes.map(item => item.value).join(':'));
			}

			if (isArray(form.roomTypes) && form.roomTypes.length) {
				tags.push(form.roomTypes.map(item => item.value).join(':'));
			}
		}

		if (isArray(form.tourOperators) && form.tourOperators.length) {
			tags.push(parseTourOperatorSelection(availableTourOperators, form.tourOperators, !!(form.disableDynamicTourOperators), !!(form.negateTourOperatorsSelect), isObject(form.occupancy)));
		} else {
			tags.push(parseTourOperatorSelection(availableTourOperators, [], !!(form.disableDynamicTourOperators), false, isObject(form.occupancy)));
		}

		if (form.railAndFly) {
			tags.push(GT_RAIL_AND_FLY);
		}

		if (form.seaView && !form.advancedSearchformData.hotelFacilities.seaView) {
			tags.push(GT_SEA_VIEW);
		}

		if (form.hygiene) {
			tags.push(GT_HYGIENE);
		}

		if (form.nonStopFlight) {
			tags.push(GT_NON_STOP_FLIGHT);
		}

		if (isNumber(form.rating) && !isNaN(form.rating)) {
			const minRating: number = form.rating;
			const rating: Array<string> = [];

			for (let i = minRating; i <= 100; i += 10) {
				rating.push(GT_RATING_PREFIX + i);
			}

			tags.push(rating.join(':'));
		}

		if (form.outboundFlightTimes.length > 0) {
			const flightTimes: Array<string> = [];

			form.outboundFlightTimes.forEach(i => {
				const time: string = i >= 0 && i <= 9 ? `0${i}` : `${i}`;
				flightTimes.push(GT_OUTBOUND_FLIGHT_TIMES_PREFIX + time);
			});

			tags.push(flightTimes.join(':'));
		}

		if (form.returnFlightTimes.length > 0) {
			const flightTimes: Array<string> = [];

			form.returnFlightTimes.forEach(i => {
				const time: string = i >= 0 && i <= 9 ? `0${i}` : `${i}`;
				flightTimes.push(GT_INBOUND_FLIGHT_TIMES_PREFIX + time);
			});

			tags.push(flightTimes.join(':'));
		}

		if (isObject(form.advancedSearchformData)) {
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.hotelType, HOTEL_TYPES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.pool, POOL_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.sport, SPORT_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.beach, BEACH_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.hotelFacilities, HOTEL_FACILITY_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.transfer, TRANSFER_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.skiArea, DISTANCE_ATTRIBUTES_MAPPING.skiArea));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.sea, DISTANCE_ATTRIBUTES_MAPPING.sea));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.trainStation, DISTANCE_ATTRIBUTES_MAPPING.trainStation));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.beach, DISTANCE_ATTRIBUTES_MAPPING.beach));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.river, DISTANCE_ATTRIBUTES_MAPPING.river));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.restaurants, DISTANCE_ATTRIBUTES_MAPPING.restaurants));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.shopping, DISTANCE_ATTRIBUTES_MAPPING.shopping));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.cityCenter, DISTANCE_ATTRIBUTES_MAPPING.cityCenter));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.busStation, DISTANCE_ATTRIBUTES_MAPPING.busStation));

			if (isArray(form.advancedSearchformData.hotelChains) && form.advancedSearchformData.hotelChains.length > 0) {
				tags.push(form.advancedSearchformData.hotelChains.map(item => item.value).join(':'));
			}
		}

		const queryModel: QueryModel = {
			adultsNumber: form.adultsNumber,
			startDate: isObject(form.dates) ? moment(form.dates.fromDate).format('YYYY-MM-DD') : '',
			endDate: isObject(form.dates) ? moment(form.dates.toDate).format('YYYY-MM-DD') : '',
			type: 'PA',
			tags: customTags.length > 0 ? customTags.join(',') : tags.join(','),
			limit: InitialQueryState.limit,
			offset: InitialQueryState.offset,
			orderBy: InitialQueryState.orderBy,
			orderDirection: InitialQueryState.orderDirection
		};

		if (isNumber(form.maxPrice) && !isNaN(form.maxPrice) && form.maxPrice > 0) {
			queryModel.maxPrice = form.maxPrice;
		}

		if (isString(hotelName) && hotelName.length) {
			queryModel.hotelName = hotelName;
		}

		if (isString(bookingCode) && bookingCode.length) {
			queryModel.bookingCode = bookingCode;
		}

		if (isString(form.geoJson) && form.geoJson.length) {
			queryModel.geoJson = form.geoJson;
		}

		if (isArray(form.topsFlops) && form.topsFlops.length) {
			queryModel.topsFlops = form.topsFlops.map(item => item.value);
		}

		if (isObject(form.occupancy)) {
			queryModel.occupancy = form.occupancy;
			queryModel.adultsNumber = form.occupancy.occupancy1.adultsNumber;

			const childAges: string = parseChildAgesToString({
				childAge1: form.occupancy.occupancy1.childAge1,
				childAge2: form.occupancy.occupancy1.childAge2,
				childAge3: form.occupancy.occupancy1.childAge3,
				childAge4: form.occupancy.occupancy1.childAge4,
			});

			if (isString(childAges) && childAges.length) {
				queryModel.childrenAges = childAges;
			}

			queryModel.multiRoomOccupancy = ['occupancy2', 'occupancy3', 'occupancy4'].map(key => {
				const multiRoom: MultiRoom = form.occupancy[key];

				if (!isNumber(multiRoom.adultsNumber) || multiRoom.adultsNumber < 1) {
					return '';
				}

				let tag: string = 'AGES';

				for (let i = 0; i < multiRoom.adultsNumber; i++) {
					tag += '-25';
				}

				['childAge1', 'childAge2', 'childAge3', 'childAge4'].forEach(ageId => {
					if (isNumber(multiRoom[ageId]) && multiRoom[ageId] >= 0 && multiRoom[ageId] < 18) {
						tag += `-${multiRoom[ageId]}`;
					}
				});

				if (isObject(multiRoom.roomType) && isString(multiRoom.roomType.id) && multiRoom.roomType.id.length > 0) {
					tag += `,${multiRoom.roomType.id}`;
				}

				if (isObject(multiRoom.boardType) && isString(multiRoom.boardType.id) && multiRoom.boardType.id.length > 0) {
					tag += `,${multiRoom.boardType.id}`;
				}

				return tag;
			}).filter(tag => tag.length > 0).join('|');
		} else {
			const childAges: string = parseChildAgesToString({
				childAge1: form.childAge1,
				childAge2: form.childAge2,
				childAge3: form.childAge3,
				childAge4: form.childAge4,
			});

			if (isString(childAges) && childAges.length) {
				queryModel.childrenAges = childAges;
			}
		}

		return new Query(queryModel);
	}

	return new Query();
};


export const createQueryFromSearchformNhModel: (
	form: SearchformNhModel,
	availableTourOperators?: Array<DropdownItem>
)  => Query = function (
	form: SearchformNhModel,
	availableTourOperators: Array<DropdownItem> = []
) {
	availableTourOperators = isArray(availableTourOperators) && !!(availableTourOperators.length) ? availableTourOperators : [];

	if (isObject(form)) {
		let tags: Array<string> = [];
		const customTags: Array<string> = [];
		let hotelName: string = null;
		let bookingCode: string = null;

		if (isArray(form.hotels) && form.hotels.length) {
			const custom: string = form.hotels.filter(item => {
				return isString(item.rawValue) && item.rawValue.startsWith('T:');
			}).map(item => item.rawValue.replace('T:', '')).join('|');

			if (custom.length) {
				custom.split('|').forEach(tagString => {
					customTags.push(tagString);
				});
			}

			const bookingCodes: Array<string> = form.hotels.filter(item => {
				return (isString(item.value) && item.value.startsWith(GT_BOOKING_CODE_PREFIX)) || (isString(item.rawValue) && item.rawValue.startsWith('#')) || (isString(item.rawValue) && item.rawValue.startsWith(':'));
			}).map(item => item.rawValue.replace('#', GT_BOOKING_CODE_PREFIX).replace(':', GT_BOOKING_CODE_PREFIX));

			if (bookingCodes.length === 1) {
				bookingCode = bookingCodes[0].replace('CODE-', '');
			} else if (bookingCodes.length > 1) {
				tags.push(bookingCodes.join(':'));
			}

			let bombyxHotelIds: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && (item.value.startsWith('GT01-') || item.value.startsWith('BT01-'));
			}).map(item => item.value);

			const manualSelectedBombyxHotelIds: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && ((item.value.startsWith('HOTELNAME-') && Number(item.rawValue)) || Number(item.value));
			}).map(item => 'GT01-' + item.rawValue);

			bombyxHotelIds = bombyxHotelIds.concat(manualSelectedBombyxHotelIds);

			if (bombyxHotelIds.length) {
				const hotelIds: Array<string> = [];

				bombyxHotelIds.forEach(hotelId => {
					hotelIds.push(hotelId);

					if (hotelId.startsWith('GT01-')) {
						hotelIds.push(hotelId.replace('GT01-', 'BT01-'));
					} else if (hotelId.startsWith('BT01-')) {
						hotelIds.push(hotelId.replace('BT01-', 'GT01-'));
					}
				});

				tags.push(hotelIds.join(':'));
			}

			const hotelNameSearchStrings: Array<string> = form.hotels.filter(item => {
				return isString(item.value) && item.value.startsWith('HOTELNAME-') && !Number(item.rawValue) && !item.rawValue.startsWith('#') && !item.rawValue.startsWith(':') && !item.rawValue.startsWith('T:');
			}).map(item => item.rawValue);

			if (hotelNameSearchStrings.length) {
				// TODO: check if we could search all search-strings
				hotelName = hotelNameSearchStrings[0];
			}
		}

		// Ignore airports if a region is selected, because nh-airports are changed to region-ids
		if (
			isArray(form.destinationAirports)
			&& form.destinationAirports.length
			&& (
				!isArray(form.destinationRegions)
				|| form.destinationRegions.length === 0
			)
		) {
			tags.push(form.destinationAirports.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationRegions) && form.destinationRegions.length) {
			tags.push(form.destinationRegions.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationCities) && form.destinationCities.length) {
			tags.push(form.destinationCities.map(item => item.value).join(':'));
		}

		if (isNumber(form.minDuration) && !isNaN(form.minDuration)) {
			const duration: Array<string> = [];
			const maxDuration: number = isNumber(form.maxDuration) && !isNaN(form.maxDuration) && form.maxDuration >= form.minDuration
				? form.maxDuration
				: form.minDuration + 2;

			for (let i = form.minDuration; i <= maxDuration; i++) {
				duration.push('DUR-' + i.toString());
			}

			tags.push(duration.join(':'));
		}

		if (isNumber(form.category) && !isNaN(form.category)) {
			const minCategory: number = form.category * 10;
			const category: Array<string> = [];

			for (let i = minCategory; i <= 70; i += 5) {
				if (i % 10 === 0) {
					category.push(GT_CATEGORY_PREFIX + (i / 10));
				} else {
					category.push(GT_CATEGORY_PREFIX + i);
				}
			}

			tags.push(category.join(':'));
		}

		if (isObject(form.occupancy)) {
			if (isObject(form.occupancy.occupancy1.roomType)) {
				tags.push(form.occupancy.occupancy1.roomType.id);
			}

			if (isObject(form.occupancy.occupancy1.boardType)) {
				tags.push(form.occupancy.occupancy1.boardType.id);
			}
		} else {
			if (isArray(form.boardTypes) && form.boardTypes.length) {
				tags.push(form.boardTypes.map(item => item.value).join(':'));
			}

			if (isArray(form.roomTypes) && form.roomTypes.length) {
				tags.push(form.roomTypes.map(item => item.value).join(':'));
			}
		}

		if (isArray(form.tourOperators) && form.tourOperators.length) {
			tags.push(parseTourOperatorSelection(availableTourOperators, form.tourOperators, false, !!(form.negateTourOperatorsSelect), isObject(form.occupancy)));
		} else {
			tags.push(parseTourOperatorSelection(availableTourOperators, [], false, false, isObject(form.occupancy)));
		}

		if (form.seaView && !form.advancedSearchformData.hotelFacilities.seaView) {
			tags.push(GT_SEA_VIEW);
		}

		if (form.hygiene) {
			tags.push(GT_HYGIENE);
		}

		if (isNumber(form.rating) && !isNaN(form.rating)) {
			const minRating: number = form.rating;
			const rating: Array<string> = [];

			for (let i = minRating; i <= 100; i += 10) {
				rating.push(GT_RATING_PREFIX + i);
			}

			tags.push(rating.join(':'));
		}

		if (isObject(form.advancedSearchformData)) {
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.hotelType, HOTEL_TYPES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.pool, POOL_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.sport, SPORT_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.beach, BEACH_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.hotelFacilities, HOTEL_FACILITY_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchCheckboxValuesToTags(form.advancedSearchformData.transfer, TRANSFER_ATTRIBUTES_MAPPING));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.skiArea, DISTANCE_ATTRIBUTES_MAPPING.skiArea));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.sea, DISTANCE_ATTRIBUTES_MAPPING.sea));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.trainStation, DISTANCE_ATTRIBUTES_MAPPING.trainStation));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.beach, DISTANCE_ATTRIBUTES_MAPPING.beach));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.river, DISTANCE_ATTRIBUTES_MAPPING.river));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.restaurants, DISTANCE_ATTRIBUTES_MAPPING.restaurants));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.shopping, DISTANCE_ATTRIBUTES_MAPPING.shopping));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.cityCenter, DISTANCE_ATTRIBUTES_MAPPING.cityCenter));
			tags = tags.concat(advancedSearchDistanceValuesToTags(form.advancedSearchformData.distances.busStation, DISTANCE_ATTRIBUTES_MAPPING.busStation));

			if (isArray(form.advancedSearchformData.hotelChains) && form.advancedSearchformData.hotelChains.length > 0) {
				tags.push(form.advancedSearchformData.hotelChains.map(item => item.value).join(':'));
			}
		}

		const queryModel: QueryModel = {
			adultsNumber: form.adultsNumber,
			startDate: isObject(form.dates) ? moment(form.dates.fromDate).format('YYYY-MM-DD') : '',
			endDate: isObject(form.dates) ? moment(form.dates.toDate).format('YYYY-MM-DD') : '',
			type: 'NH',
			tags: customTags.length > 0 ? customTags.join(',') : tags.join(','),
			limit: InitialQueryState.limit,
			offset: InitialQueryState.offset,
			orderBy: InitialQueryState.orderBy,
			orderDirection: InitialQueryState.orderDirection
		};

		if (isNumber(form.maxPrice) && !isNaN(form.maxPrice) && form.maxPrice > 0) {
			queryModel.maxPrice = form.maxPrice;
		}

		if (isString(hotelName) && hotelName.length) {
			queryModel.hotelName = hotelName;
		}

		if (isString(bookingCode) && bookingCode.length) {
			queryModel.bookingCode = bookingCode;
		}

		if (isString(form.geoJson) && form.geoJson.length) {
			queryModel.geoJson = form.geoJson;
		}

		if (isArray(form.topsFlops) && form.topsFlops.length) {
			queryModel.topsFlops = form.topsFlops.map(item => item.value);
		}

		if (isObject(form.occupancy)) {
			queryModel.occupancy = form.occupancy;
			queryModel.adultsNumber = form.occupancy.occupancy1.adultsNumber;

			const childAges: string = parseChildAgesToString({
				childAge1: form.occupancy.occupancy1.childAge1,
				childAge2: form.occupancy.occupancy1.childAge2,
				childAge3: form.occupancy.occupancy1.childAge3,
				childAge4: form.occupancy.occupancy1.childAge4,
			});

			if (isString(childAges) && childAges.length) {
				queryModel.childrenAges = childAges;
			}

			queryModel.multiRoomOccupancy = ['occupancy2', 'occupancy3', 'occupancy4'].map(key => {
				const multiRoom: MultiRoom = form.occupancy[key];

				if (!isNumber(multiRoom.adultsNumber) || multiRoom.adultsNumber < 1) {
					return '';
				}

				let tag: string = 'AGES';

				for (let i = 0; i < multiRoom.adultsNumber; i++) {
					tag += '-25';
				}

				['childAge1', 'childAge2', 'childAge3', 'childAge4'].forEach(ageId => {
					if (isNumber(multiRoom[ageId]) && multiRoom[ageId] >= 0 && multiRoom[ageId] < 18) {
						tag += `-${multiRoom[ageId]}`;
					}
				});

				if (isObject(multiRoom.roomType) && isString(multiRoom.roomType.id) && multiRoom.roomType.id.length > 0) {
					tag += `,${multiRoom.roomType.id}`;
				}

				if (isObject(multiRoom.boardType) && isString(multiRoom.boardType.id) && multiRoom.boardType.id.length > 0) {
					tag += `,${multiRoom.boardType.id}`;
				}

				return tag;
			}).filter(tag => tag.length > 0).join('|');
		} else {
			const childAges: string = parseChildAgesToString({
				childAge1: form.childAge1,
				childAge2: form.childAge2,
				childAge3: form.childAge3,
				childAge4: form.childAge4,
			});

			if (isString(childAges) && childAges.length) {
				queryModel.childrenAges = childAges;
			}
		}

		return new Query(queryModel);
	}

	return new Query();
};


export const createQueryFromSearchformNfModel: (form: SearchformNfModel, availableTourOperators?: Array<DropdownItem>) => Query = function (
	form: SearchformNfModel,
	availableTourOperators: Array<DropdownItem> = []
) {
	availableTourOperators = isArray(availableTourOperators) && !!(availableTourOperators.length) ? availableTourOperators : [];

	if (isObject(form)) {
		const tags: Array<string> = [];

		if (isArray(form.departureAirports) && form.departureAirports.length) {
			tags.push(form.departureAirports.map(item => item.value).join(':'));
		}

		if (isArray(form.destinationAirports) && form.destinationAirports.length) {
			tags.push(form.destinationAirports.map(item => item.value).join(':'));
		}

		if (isArray(form.tourOperators) && form.tourOperators.length) {
			tags.push(parseTourOperatorSelection(availableTourOperators, form.tourOperators, false, !!(form.negateTourOperatorsSelect), false));
		} else {
			tags.push(parseTourOperatorSelection(availableTourOperators, [], false, false, false));
		}

		if (form.outboundAndReturnFlight) {
			if (isNumber(form.minDuration) && !isNaN(form.minDuration)) {
				const duration: Array<string> = [];
				const maxDuration: number = isNumber(form.maxDuration) && !isNaN(form.maxDuration) && form.maxDuration >= form.minDuration
					? form.maxDuration
					: form.minDuration + 2;

				for (let i = form.minDuration; i <= maxDuration; i++) {
					duration.push('DUR-' + i.toString());
				}

				tags.push(duration.join(':'));
			}
		} else {
			tags.push('DUR-0');
		}

		const queryModel: QueryModel = {
			adultsNumber: form.adultsNumber,
			startDate: isObject(form.dates) ? moment(form.dates.fromDate).format('YYYY-MM-DD') : '',
			endDate: isObject(form.dates) ? moment(form.dates.toDate).format('YYYY-MM-DD') : '',
			type: 'NF',
			tags: tags.join(','),
			limit: InitialQueryState.limit,
			offset: InitialQueryState.offset,
			orderBy: InitialQueryState.orderBy,
			orderDirection: InitialQueryState.orderDirection
		};

		if (isNumber(form.maxPrice) && !isNaN(form.maxPrice) && form.maxPrice > 0) {
			queryModel.maxPrice = form.maxPrice;
		}

		const childAges: string = parseChildAgesToString({
			childAge1: form.childAge1,
			childAge2: form.childAge2,
			childAge3: form.childAge3,
			childAge4: form.childAge4,
		});

		if (isString(childAges) && childAges.length) {
			queryModel.childrenAges = childAges;
		}

		return new Query(queryModel);
	}

	return new Query();
};


function parseChildAgesToString(ages: { childAge1: number; childAge2: number; childAge3: number; childAge4: number; }): string {
	const childAges: Array<number> = [];

	for (let i = 1; i < 5; i++) {
		const childId: string = 'childAge' + i;

		if (isNumber(ages[childId]) && !isNaN(ages[childId])) {
			const age: number = parseInt(ages[childId], 10);

			if (age >= 0 && age <= 17) {
				childAges.push(age);
			}
		}
	}

	return childAges.join(',');
}

function parseTourOperatorSelection(
	availableTourOperators: Array<DropdownItem>,
	tourOperatorSelection: Array<DropdownItem>,
	disableDynamicTourOperators: boolean,
	negateSelection: boolean,
	isMultiRoomSearch: boolean
): string {
	let tourOperators = tourOperatorSelection.length > 0 && !negateSelection ? tourOperatorSelection : availableTourOperators;

	if (negateSelection) {
		tourOperators = tourOperators.filter(top => !(tourOperatorSelection.some(topS => topS.value === top.value)));
	}

	if (disableDynamicTourOperators) {
		tourOperators = tourOperators.filter(top => !isArray(top.flags) || top.flags.indexOf('X') === -1);
	}

	if (isMultiRoomSearch) {
		tourOperators = tourOperators.filter(top => !MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.includes(top.rawValue));
	}

	if (tourOperatorSelection.length > 0 && tourOperators.length === 0) {
		return 'TOP-FALSE';
	}

	return tourOperators.map(top => top.value).join(':');
}

function advancedSearchCheckboxValuesToTags(values: any|KeyValueObject<boolean>, mapping: KeyValueObject<string>): Array<string> {
	const tags: Array<string> = [];

	Object.keys(values).forEach(key => {
		const value = values[key];

		if (value === true && has(mapping, key)) {
			tags.push(mapping[key]);
		}
	});

	return tags;
}

function advancedSearchDistanceValuesToTags(distance: AdvancedSearchformDistance, globalType: string): Array<string> {
	if (!isString(globalType) || globalType.length === 0 || isEqual(distance, DEFAULT_ADVANCED_SEARCHFORM_DISTANCE)) {
		return [];
	}

	const tags: Array<string> = [];
	const max = getDistanceIndexToValue(distance.max);

	for (let i = 0; i <= max; i++) {
		if (i === 0) {
			continue;
		}

		tags.push(globalType + DISTANCE_TYPES[i]);
	}

	return [tags.join(':')];
}



// WEBPACK FOOTER //
// ./src/app/xena/core/shared/models/query/query.model.ts